import React, { useState, useMemo } from 'react';
import './CSLAnnotatedStructure.css';

interface CorporateEntity {
  legal_name: string;
  relationship: string;
  country?: string | null;
  city?: string | null;
  industry?: string | null;
  description?: string | null;
  ownership_pct?: string | null;
  confidence?: string;
  csl_status?: 'MATCHED' | 'CLEAR';
  discovered_via?: string;
}

interface MatchValidation {
  match_index: number;
  match_name: string;
  is_same_entity: boolean | null;
  relationship: string;
  confidence: string;
  reasoning: string;
  rechecked?: boolean;
  recheck_source_url?: string;
  recheck_source_title?: string;
}

interface SearchResult {
  name: string;
  source: string;
  type: string;
  programs?: string[];
}

interface CSLAnnotatedStructureProps {
  structure: {
    root_entity: string;
    entities: CorporateEntity[];
    relationships: Array<{ parent: string; child: string; type: string }>;
    sources?: Array<{ title: string; url: string }>;
  };
  matchValidations: MatchValidation[];
  cslMatches: SearchResult[];
}

function safeExternalUrl(url: string): string | null {
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

interface TreeNodeData {
  entity: CorporateEntity;
  children: TreeNodeData[];
}

function buildTree(structure: CSLAnnotatedStructureProps['structure']): TreeNodeData | null {
  const { entities, relationships, root_entity } = structure;
  if (entities.length === 0) return null;

  const entityMap = new Map<string, CorporateEntity>();
  for (const e of entities) {
    entityMap.set(e.legal_name, e);
  }

  const childrenMap = new Map<string, string[]>();
  for (const rel of relationships) {
    if (!childrenMap.has(rel.parent)) {
      childrenMap.set(rel.parent, []);
    }
    childrenMap.get(rel.parent)!.push(rel.child);
  }

  let rootName = root_entity;
  if (!entityMap.has(rootName) && entities.length > 0) {
    const parentEntity = entities.find((e) => e.relationship === 'PARENT');
    rootName = parentEntity ? parentEntity.legal_name : entities[0].legal_name;
  }

  const visited = new Set<string>();
  function buildNode(name: string): TreeNodeData | null {
    if (visited.has(name)) return null;
    visited.add(name);

    const entity = entityMap.get(name);
    if (!entity) return null;

    const childNames = childrenMap.get(name) || [];
    const children: TreeNodeData[] = [];
    for (const childName of childNames) {
      const childNode = buildNode(childName);
      if (childNode) children.push(childNode);
    }
    return { entity, children };
  }

  const tree = buildNode(rootName);
  if (tree) {
    for (const entity of entities) {
      if (!visited.has(entity.legal_name)) {
        tree.children.push({ entity, children: [] });
      }
    }
  }
  return tree;
}

const CSLAnnotatedStructure: React.FC<CSLAnnotatedStructureProps> = ({
  structure,
  matchValidations,
  cslMatches,
}) => {
  const tree = useMemo(() => {
    const built = buildTree(structure);
    if (built) return built;
    // Fallback: show root entity as a single-node tree
    if (structure.root_entity) {
      const fallbackEntity: CorporateEntity = {
        legal_name: structure.root_entity,
        relationship: 'SELF',
        confidence: 'HIGH',
      };
      return { entity: fallbackEntity, children: [] } as TreeNodeData;
    }
    return null;
  }, [structure]);

  // Build lookup: entity name -> matching validations
  const entityMatchMap = useMemo(() => {
    const map = new Map<string, { validation: MatchValidation; cslMatch: SearchResult }[]>();

    for (const v of matchValidations) {
      if (v.relationship === 'EXACT' || v.relationship === 'SUBSIDIARY' || v.relationship === 'AFFILIATED') {
        const matchIdx = v.match_index - 1;
        const cslMatch = matchIdx >= 0 && matchIdx < cslMatches.length ? cslMatches[matchIdx] : null;
        if (!cslMatch) continue;

        // Try to match to a structure entity by name
        for (const entity of structure.entities) {
          const en = entity.legal_name.toLowerCase();
          const mn = v.match_name.toLowerCase();
          if (en.includes(mn) || mn.includes(en) || _tokenOverlap(en, mn) >= 0.7) {
            const key = entity.legal_name;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push({ validation: v, cslMatch });
          }
        }
      }
    }
    return map;
  }, [matchValidations, cslMatches, structure.entities]);

  const matchedCount = useMemo(
    () => structure.entities.filter(e => e.csl_status === 'MATCHED' || entityMatchMap.has(e.legal_name)).length,
    [structure.entities, entityMatchMap]
  );

  if (!tree) return null;

  return (
    <div className="csl-annotated-structure">
      <h4 className="csl-annotated-header">
        Corporate Structure &mdash; CSL Status
        <span className="csl-annotated-summary">
          {matchedCount > 0
            ? `${matchedCount} of ${structure.entities.length} entities matched`
            : `All ${structure.entities.length} entities clear`}
        </span>
      </h4>
      <div className="csl-annotated-tree">
        <AnnotatedTreeNode
          node={tree}
          entityMatchMap={entityMatchMap}
        />
      </div>
    </div>
  );
};

interface AnnotatedTreeNodeProps {
  node: TreeNodeData;
  entityMatchMap: Map<string, { validation: MatchValidation; cslMatch: SearchResult }[]>;
}

const AnnotatedTreeNode: React.FC<AnnotatedTreeNodeProps> = ({ node, entityMatchMap }) => {
  const { entity, children } = node;
  const [expanded, setExpanded] = useState(false);

  const matches = entityMatchMap.get(entity.legal_name) || [];
  const isMatched = entity.csl_status === 'MATCHED' || matches.length > 0;
  const isReclassified = entity.discovered_via === 'csl_recheck';

  return (
    <div className="annotated-tree-node">
      <div
        className={`annotated-node-card ${isMatched ? 'csl-matched' : 'csl-clear'}`}
        onClick={matches.length > 0 ? () => setExpanded(!expanded) : undefined}
        role={matches.length > 0 ? 'button' : undefined}
        tabIndex={matches.length > 0 ? 0 : undefined}
        onKeyDown={matches.length > 0 ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } } : undefined}
      >
        <span className={`csl-status-icon ${isMatched ? 'matched' : 'clear'}`}>
          {isMatched ? (
            <svg viewBox="0 0 20 20" width="16" height="16" aria-label="CSL Match">
              <path d="M10 2L12.5 7.5L18 8.5L14 12.5L15 18L10 15.5L5 18L6 12.5L2 8.5L7.5 7.5L10 2Z"
                fill="currentColor" />
            </svg>
          ) : (
            <svg viewBox="0 0 20 20" width="16" height="16" aria-label="Clear">
              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.5-10.5l-5 5m0-5l5 5"
                stroke="currentColor" fill="none" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </span>
        <div className="annotated-node-content">
          <span className="annotated-node-name">
            {entity.legal_name}
            {matches.length > 0 && <span className="expand-hint">{expanded ? '\u25BE' : '\u25B8'}</span>}
          </span>
          <div className="annotated-node-meta">
            <span className={`relationship-type-badge ${entity.relationship.toLowerCase()}`}>
              {entity.relationship.replace('_', ' ')}
            </span>
            {entity.country && <span className="annotated-country">{entity.country}</span>}
            {entity.ownership_pct && <span className="annotated-ownership">{entity.ownership_pct}</span>}
            {isReclassified && (
              <span className="reclassified-badge">Reclassified</span>
            )}
          </div>
        </div>
        <span className={`csl-status-badge ${isMatched ? 'matched' : 'clear'}`}>
          {isMatched ? 'CSL MATCH' : 'CLEAR'}
        </span>
      </div>

      {/* Expanded CSL match detail */}
      {expanded && matches.length > 0 && (
        <div className="annotated-match-details">
          {matches.map((m, idx) => (
            <div key={idx} className="annotated-match-item">
              <p><strong>CSL Entry:</strong> {m.cslMatch.name}</p>
              <p><strong>List:</strong> {m.cslMatch.source}</p>
              {m.cslMatch.programs && m.cslMatch.programs.length > 0 && (
                <p><strong>Programs:</strong> {m.cslMatch.programs.join(', ')}</p>
              )}
              <p><strong>Relationship:</strong> {m.validation.relationship} ({m.validation.confidence})</p>
              <p><strong>Analysis:</strong> {m.validation.reasoning}</p>
              {m.validation.rechecked && m.validation.recheck_source_url && (
                <p className="recheck-source">
                  <strong>Source:</strong>{' '}
                  {(() => {
                    const href = safeExternalUrl(m.validation.recheck_source_url);
                    return href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {m.validation.recheck_source_title || 'View source'}
                      </a>
                    ) : (
                      <span>{m.validation.recheck_source_title || m.validation.recheck_source_url}</span>
                    );
                  })()}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Children */}
      {children.length > 0 && (
        <div className="annotated-tree-children">
          {children.map((child) => (
            <AnnotatedTreeNode
              key={child.entity.legal_name}
              node={child}
              entityMatchMap={entityMatchMap}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function _tokenOverlap(a: string, b: string): number {
  const stopWords = new Set(['co', 'ltd', 'inc', 'corp', 'llc', 'the', 'of', 'and', 'company', 'limited', 'corporation', 'group']);
  const tokensA = new Set(a.replace(/[,.]/g, ' ').split(/\s+/).filter(w => w.length > 1 && !stopWords.has(w)));
  const tokensB = new Set(b.replace(/[,.]/g, ' ').split(/\s+/).filter(w => w.length > 1 && !stopWords.has(w)));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let overlap = 0;
  tokensA.forEach(t => { if (tokensB.has(t)) overlap++; });
  const smaller = Math.min(tokensA.size, tokensB.size);
  return smaller > 0 ? overlap / smaller : 0;
}

export default CSLAnnotatedStructure;
